import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { createHead } from "@vueuse/head";
import { Exceptionless } from '@exceptionless/vue'
import { useExceptionless } from './composables/useExceptionless'
import { Environment } from "@/utils/environment";
import { watch } from 'vue'
import TenantConfigPlugin from './plugins/tenant-config-plugin'
import TenantConfigService from './services/tenant-config-service'
import { initGtm, initGtmNoscriptFallback, pushDataLayer } from './utils/gtm'
import { useAuthStore } from './stores/auth'

const app = createApp(App)
const pinia = createPinia()
const head = createHead();

app.use(head);
app.use(pinia)
app.use(router)
app.use(TenantConfigPlugin)

// Load the CookieFirst consent script as early as possible, before the app
// mounts, so it can gate other scripts/cookies as intended by the vendor.
try {
    const cookieCMPLink = (TenantConfigService.features as any)?.cookieCMPLink;
    if (cookieCMPLink && Environment.isProduction && !document.querySelector(`script[src="${cookieCMPLink}"]`)) {
        const script = document.createElement('script');
        script.src = cookieCMPLink;
        script.async = true;
        script.onload = () => console.log('[CookieFirst] Script loaded successfully.');
        script.onerror = () => console.error('[CookieFirst] Failed to load script:', cookieCMPLink);
        document.head.appendChild(script);
    }
} catch (error) {
    console.error('[CookieFirst] Failed to initialize cookie consent script:', error);
}

// Seed dataLayer and inject GTM as early as possible, before the app mounts.
// The seed push must happen before initGtm() so isLoggedIn/memberId are
// already present in dataLayer when GTM's own gtm.js snippet first runs.
try {
    const gtmId = TenantConfigService.features?.gtmContainerId;
    if (gtmId) {
        const authStore = useAuthStore(pinia);

        pushDataLayer({
            isLoggedIn: authStore.isAuthenticated,
            memberId: authStore.member.memberId ?? '',
        });

        initGtm(gtmId);
        initGtmNoscriptFallback(gtmId);

        // Keep dataLayer in sync reactively on login/logout, not just at boot.
        watch(
            () => [authStore.isAuthenticated, authStore.member.memberId] as const,
            ([isLoggedIn, memberId]) => {
                pushDataLayer({ isLoggedIn, memberId: memberId ?? '' });

                // Fire a dedicated event once memberId is known, so GTM triggers
                // gated on memberId (e.g. consent-based marketing tags) don't have
                // to rely on page_view timing, which can lag behind consent events
                // that fire very early in the page lifecycle.
                if (isLoggedIn && memberId) {
                    pushDataLayer({ event: 'member_identified' });
                }
            }
        );
    }
} catch (error) {
    console.error('[GTM] Failed to initialize Google Tag Manager:', error);
}

let exceptionlessKey = Environment.isProduction ? "OOZaUWgzlRMwYWKukYeF5yjpgJMOmz4ctwP58iDY" : "7uuhSGy5kiAZxid5MMPw4tT24Rv5EDXqNxH9frOU"

// 1. Start Exceptionless
Exceptionless.startup((c) => {
    c.apiKey = exceptionlessKey;
    c.serverUrl = 'https://collector.exceptionless.io'; // optional, defaults to Exceptionless.io
    c.setUserIdentity('frontend-user'); // optional - customize with actual user if you can
    c.defaultData['environment'] = process.env.NODE_ENV || 'development';
});

// 2. Register Vue's global error handler
app.config.errorHandler = (err, instance, info) => {
    if (reloadOnceForStaleChunk(err)) return;
    const { logError } = useExceptionless(); logError(err, `Vue Global: ${info}`, ['vue']);
    if (isChunkLoadError(err)) return; // Error500 is lazy-loaded too; redirecting would loop
    if (!router.currentRoute.value.path.startsWith('/errors')) router.replace('/errors/Error500');
};

// Stale chunk handling: after a deployment swap, old hashed files may no longer exist.
// Reload once per page session to pick up the new index.html (flag is cleared after a successful mount).
function isChunkLoadError(err: any): boolean {
    const msg = String(err?.message ?? err ?? '');
    return /Loading chunk|Loading CSS chunk|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(msg);
}

const chunkReloadKey = 'chunk-reload-attempted';

// Returns true only when a reload was triggered; otherwise callers log and handle the error as normal.
function reloadOnceForStaleChunk(err: any): boolean {
    if (!isChunkLoadError(err)) return false;
    try {
        if (!sessionStorage.getItem(chunkReloadKey)) {
            sessionStorage.setItem(chunkReloadKey, '1');
            window.location.reload();
            return true;
        }
    } catch {
        // sessionStorage unavailable: don't reload, to avoid a possible loop
    }
    return false;
}

router.onError((err) => {
    if (reloadOnceForStaleChunk(err)) return;
    if (isChunkLoadError(err)) {
        const { logError } = useExceptionless(); logError(err, 'Stale chunk after reload', ['router', 'chunk']);
    }
});

// 3. Register global browser-level error handlers
window.addEventListener('error', (event) => {
    const { logError } = useExceptionless();
    logError(event.error || event, 'Window Error Event', ['window', 'error']);
});

window.addEventListener('unhandledrejection', (event) => {
    if (reloadOnceForStaleChunk(event.reason)) return;
    const { logError } = useExceptionless(); logError(event.reason, 'Unhandled Rejection', ['promise']);
    if (isChunkLoadError(event.reason)) return;
    if (!router.currentRoute.value.path.startsWith('/errors')) router.replace('/errors/Error500');
});

app.mount('#app')

router.isReady().then(() => {
    try { sessionStorage.removeItem(chunkReloadKey); } catch { /* ignore */ }
}).catch(() => { /* initial navigation failure is handled by router.onError */ });
